/**
 * Форматирование чисел и дат на языке интерфейса (ADR-0019), точность честная.
 *
 * Числа — `Intl` локали интерфейса (`runtimeLocale`); месяцы, порядок частей
 * даты и подписи единиц — строки каталога (`units.*`): у CLDR узбекские месяцы
 * пишутся с ‘ (U+2018) вместо официального ʻ, а тонны и кубометра в `Intl`
 * нет. Функции зовутся при рендере, поэтому смена языка их подхватывает.
 */

import type { Crop, Cycle } from '../data/types';
import { cropLabel, cropName } from '../../lib/crops';
import { defineMessages } from '../../i18n/define';
import type { Message } from '../../i18n/define';
import { runtimeIntl, runtimeLocale, tr, numberLocale } from '../../i18n/runtime';
import { localIsoDate } from '../../lib/localDate';
import type { MessageId } from '../../i18n/messageIds.generated';

const M = defineMessages({
  monthShort: {
    id: 'units.month.short',
    defaultMessage:
      '{month, select, 1 {янв} 2 {фев} 3 {мар} 4 {апр} 5 {мая} 6 {июн} 7 {июл} 8 {авг} 9 {сен} 10 {окт} 11 {ноя} 12 {дек} other {}}',
    description: 'Короткое имя месяца на шкалах и осях («авг»), НЕ в дате с числом: там units.month.inDate; {month} — номер месяца 1–12',
  },
  monthInDate: {
    id: 'units.month.inDate',
    defaultMessage:
      '{month, select, 1 {янв} 2 {фев} 3 {мар} 4 {апр} 5 {мая} 6 {июн} 7 {июл} 8 {авг} 9 {сен} 10 {окт} 11 {ноя} 12 {дек} other {}}',
    description:
      'Имя месяца в дате с числом: «26 авг» (без года), «26 авг 2026»; {month} — номер 1–12. По-русски сокращённое, по-узбекски ПОЛНОЕ («26-avgust»): к дате слитно клеятся окончания -gacha/-dan/-da/-ga («26-avgustgacha»), а к сокращению они дают несуществующее «26-avggacha». Сокращённое имя для шкал и календаря — units.month.short',
  },
  monthLong: {
    id: 'units.month.long',
    defaultMessage:
      '{month, select, 1 {января} 2 {февраля} 3 {марта} 4 {апреля} 5 {мая} 6 {июня} 7 {июля} 8 {августа} 9 {сентября} 10 {октября} 11 {ноября} 12 {декабря} other {}}',
    description: 'Полное имя месяца в дате «26 августа 2026» (по-русски — родительный падеж); {month} — номер 1–12',
  },
  dateDay: { id: 'units.date.day', defaultMessage: '{day} {month}', description: 'Дата без года: «26 авг», «26-avgust»; {month} — имя месяца в дате (units.month.inDate): по-русски сокращённое, по-узбекски полное' },
  dateShort: { id: 'units.date.short', defaultMessage: '{day} {month} {year}', description: 'Короткая дата: «26 авг 2026», «26-avgust 2026»; {month} — имя месяца в дате (units.month.inDate)' },
  dateLong: { id: 'units.date.long', defaultMessage: '{day} {month} {year}', description: 'Полная дата: «26 августа 2026»; {month} — полное имя месяца' },
  windowSameYear: {
    id: 'units.window.sameYear',
    defaultMessage: '{from} — {to} {year}',
    description: 'Окно дат внутри одного года: «1 апр — 15 мая 2026»; {from}/{to} — даты без года',
  },
  windowRange: { id: 'units.window.range', defaultMessage: '{from} — {to}', description: 'Окно дат разных лет: «1 окт 2025 — 10 июл 2026»' },
  percent: { id: 'units.percent', defaultMessage: '{value} %', description: 'Процент после числа: «78 %»; {value} — число' },
  haValue: { id: 'units.ha.value', defaultMessage: '{value} га', description: 'Гектары с единицей: «46 738,78 га»' },
  m3Value: { id: 'units.m3.value', defaultMessage: '{value} м³', description: 'Кубометры с единицей: «1 240 000 м³»' },
  m3Million: { id: 'units.m3.million', defaultMessage: '{value} млн м³', description: 'Миллионы кубометров над столбиком: «1,2 млн м³»' },
  m3Thousand: { id: 'units.m3.thousand', defaultMessage: '{value} тыс. м³', description: 'Тысячи кубометров над столбиком: «320 тыс. м³»' },
});

const NBSP = '\u00a0';
/** Пробелы подписи единицы — неразрывные: единица не отрывается от числа. */
const glue = (text: string) => text.replace(/ /g, NBSP);

const formats = new Map<string, Intl.NumberFormat>();
const nf = (min: number, max: number) => {
  const locale = runtimeLocale();
  const key = `${locale}|${min}|${max}`;
  let format = formats.get(key);
  if (!format) {
    format = new Intl.NumberFormat(numberLocale(), { minimumFractionDigits: min, maximumFractionDigits: max });
    formats.set(key, format);
  }
  return format;
};

/** Целое: «10 812». */
export function num(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return nf(0, 0).format(value);
}

/** Гектары с двумя знаками: «46 738,78». Единица подаётся отдельно. */
export function ha(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return nf(2, 2).format(value);
}

/** Гектары с единицей: «46 738,78 га». */
export function haUnit(value: number | null | undefined): string {
  const v = ha(value);
  return v === '—' ? v : tr(M.haValue, { value: v });
}

/**
 * Доля в процентах: один знак после запятой по умолчанию, «78,3 %» — точность
 * слоя метрик и ответа ИИ (№1654). Целые проценты — только явным `digits = 0`
 * там, где это не доля метрики (например, уверенность сопоставления).
 */
export function pct(value: number | null | undefined, digits = 1): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return tr(M.percent, { value: nf(digits, digits).format(value) });
}

/**
 * Точность долей двух осей — один знак после запятой, одинаково на всех
 * экранах. Одна величина не должна на главной читаться «38 %», а в аналитике
 * «37,5 %»: расхождение округления читается как расхождение данных.
 */
export const AXIS_DIGITS = 1;

/** Доля оси «отчитались / без отчёта»: «37,5 %». */
export function pctAxis(value: number | null | undefined): string {
  return pct(value, AXIS_DIGITS);
}

/**
 * Значение доли без знака «%» (единица подписана рядом, как у KPI): до одного
 * знака после запятой — точность слоя метрик и ответа ИИ, «94,7», «95» (№1654).
 * Не `num(Math.round(…))`: плитка «95 %» при «94,7 %» у ИИ читалась расхождением.
 */
export function pctValue(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return nf(0, AXIS_DIGITS).format(value);
}

/** Доля от знаменателя в процентах; знаменатель 0 → null (нечего делить). */
export function share(part: number, total: number): number | null {
  if (!Number.isFinite(part) || !Number.isFinite(total) || total <= 0) return null;
  return (part / total) * 100;
}

/** Кубометры: «1 240 000 м³». */
export function m3(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  return glue(tr(M.m3Value, { value: nf(0, 0).format(Math.round(value)) }));
}

/**
 * Кубометры для подписи над столбиком: «1,2 млн м³», «320 тыс. м³».
 * Крупная единица честнее обрезания разрядов: точное число живёт в подсказке
 * и в строках рядом, подпись только даёт порядок величины. Пробелы внутри —
 * неразрывные: единица не отрывается от числа переносом строки
 * («8,0 млн / м³», agroai-6al.38).
 */
export function m3Compact(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '—';
  const abs = Math.abs(value);
  if (abs >= 1_000_000) return glue(tr(M.m3Million, { value: nf(1, 1).format(value / 1_000_000) }));
  if (abs >= 10_000) return glue(tr(M.m3Thousand, { value: nf(0, 0).format(Math.round(value / 1000)) }));
  return m3(value);
}

function parseIso(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const parts = iso.slice(0, 10).split('-');
  if (parts.length !== 3) return null;
  const [y, m, d] = parts.map(Number) as [number, number, number];
  if (!Number.isFinite(y) || !Number.isFinite(m) || !Number.isFinite(d)) return null;
  return new Date(Date.UTC(y, m - 1, d));
}

/** Короткое имя месяца («авг») для подписей шкал. В дате с числом — `monthInDate`. */
export const monthShort = (d: Date) => tr(M.monthShort, { month: String(d.getUTCMonth() + 1) });

/** Имя месяца в дате с числом: «авг» по-русски, «avgust» по-узбекски (окончания клеятся к дате слитно). */
const monthInDate = (d: Date) => tr(M.monthInDate, { month: String(d.getUTCMonth() + 1) });

/** Короткая дата: «26 авг 2026», «26-avgust 2026». */
export function dateShort(iso: string | null | undefined): string {
  const d = parseIso(iso);
  if (!d) return '—';
  return tr(M.dateShort, { day: d.getUTCDate(), month: monthInDate(d), year: String(d.getUTCFullYear()) });
}

/** Дата без года, для окон внутри сезона: «26 авг», «26-avgust». */
export function dateDay(iso: string | null | undefined): string {
  const d = parseIso(iso);
  if (!d) return '—';
  return tr(M.dateDay, { day: d.getUTCDate(), month: monthInDate(d) });
}

/** Полная дата: «26 августа 2026». */
export function dateLong(iso: string | null | undefined): string {
  const d = parseIso(iso);
  if (!d) return '—';
  return tr(M.dateLong, {
    day: d.getUTCDate(),
    month: tr(M.monthLong, { month: String(d.getUTCMonth() + 1) }),
    year: String(d.getUTCFullYear()),
  });
}

/** Час и минуты момента: «14:30» — местное время, 24 часа, одинаково на всех языках. */
export function clock(at: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(at.getHours())}:${pad(at.getMinutes())}`;
}

export interface StampOptions {
  /** `short` — «9 окт.» / «9-oktabr»; `long` — «9 октября» / «9-oktabr» (с годом по-узбекски: «2026-yil 9-oktabr»). */
  month?: 'short' | 'long';
  year?: boolean;
  time?: boolean;
}

/**
 * Момент времени (метка записи, версия, беседа), а не календарный день:
 * «9 окт., 14:30», «9 октября 2026 г., 14:30», «9-oktabr, 14:30». Русский — `Intl`, как
 * раньше; узбекский — месяцы каталога (`units.*`), те же, что у остальных дат интерфейса:
 * `Intl` для `uz` есть не в каждом браузере и печатает сокращение «okt»/«окт» или английский
 * «Oct». Пусто — когда даты нет или она не разбирается.
 */
export function stamp(value: string | Date | null | undefined, { month = 'short', year = false, time = false }: StampOptions = {}): string {
  if (!value) return '';
  const at = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(at.getTime())) return '';
  if (runtimeLocale() === 'ru') {
    return runtimeIntl().formatDate(at, {
      day: 'numeric',
      month,
      ...(year ? { year: 'numeric' as const } : {}),
      ...(time ? { hour: '2-digit' as const, minute: '2-digit' as const } : {}),
    });
  }
  const iso = localIsoDate(at);
  const day = month === 'long' ? dateLong(iso) : year ? dateShort(iso) : dateDay(iso);
  return time ? `${day}, ${clock(at)}` : day;
}

/** Окно: «1 апр — 15 мая 2026». Год печатается один раз, если он общий. */
export function windowLabel(from: string | null, to: string | null): string {
  const a = parseIso(from);
  const b = parseIso(to);
  if (!a || !b) return '—';
  if (a.getUTCFullYear() === b.getUTCFullYear()) {
    return tr(M.windowSameYear, { from: dateDay(from), to: dateDay(to), year: String(b.getUTCFullYear()) });
  }
  return tr(M.windowRange, { from: dateShort(from), to: dateShort(to) });
}

function dd(d: Date): string {
  const day = `${d.getUTCDate()}`.padStart(2, '0');
  const month = `${d.getUTCMonth() + 1}`.padStart(2, '0');
  return `${day}.${month}`;
}

/**
 * Окно в одну строку для узкой колонки: «01.03–20.03». Разные годы получают
 * два знака года («01.10.25–10.07.26»), иначе окно читалось бы как однолетнее.
 */
export function windowShort(from: string | null, to: string | null): string {
  const a = parseIso(from);
  const b = parseIso(to);
  if (!a || !b) return '—';
  if (a.getUTCFullYear() === b.getUTCFullYear()) return `${dd(a)}–${dd(b)}`;
  const ya = `${a.getUTCFullYear()}`.slice(2);
  const yb = `${b.getUTCFullYear()}`.slice(2);
  return `${dd(a)}.${ya}–${dd(b)}.${yb}`;
}

/**
 * Сообщение-счёт: ICU `{count, plural, …}` с аргументом `count` (ADR-0019).
 * С `#` — «10 812 контуров», без `#` — только слово («контуров»).
 */
export type CountMessage = Message<MessageId>;

/** Русское склонение по числу (только русский): plural(3, ['контур','контура','контуров']). */
export function plural(value: number, forms: [string, string, string] | CountMessage): string {
  if (!Array.isArray(forms)) return tr(forms as Message<'common.count.contour'>, { count: value });
  const abs = Math.abs(Math.trunc(value));
  const mod10 = abs % 10;
  const mod100 = abs % 100;
  if (mod10 === 1 && mod100 !== 11) return forms[0];
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return forms[1];
  return forms[2];
}

/**
 * «10 812 контуров» — число плюс согласованное слово. Передавайте сообщение
 * каталога с `#` (`WORDS.contour` или своё `{count, plural, …}`); массив
 * русских форм — устаревшая форма, на узбекском он остался бы русским.
 */
export function countOf(value: number, forms: [string, string, string] | CountMessage): string {
  if (!Array.isArray(forms)) return tr(forms as Message<'common.count.contour'>, { count: value });
  return `${num(value)} ${plural(value, forms)}`;
}

/**
 * Счётные слова приложения — сообщения каталога с числом (`#`). По-узбекски
 * существительное после числа не меняется («5 kontur»), поэтому у перевода
 * формы `one` и `other` обычно совпадают.
 */
export const WORDS = defineMessages({
  contour: {
    id: 'common.count.contour',
    defaultMessage: '{count, plural, one {# контур} few {# контура} many {# контуров} other {# контура}}',
    description: 'Число контуров (участков земли по кадастру)',
  },
  farm: {
    id: 'common.count.farm',
    defaultMessage: '{count, plural, one {# хозяйство} few {# хозяйства} many {# хозяйств} other {# хозяйства}}',
    description: 'Число хозяйств (фермерских хозяйств)',
  },
  massif: {
    id: 'common.count.massif',
    defaultMessage: '{count, plural, one {# массив} few {# массива} many {# массивов} other {# массива}}',
    description: 'Число массивов (группа хозяйств и земель района)',
  },
  work: {
    id: 'common.count.work',
    defaultMessage: '{count, plural, one {# работа} few {# работы} many {# работ} other {# работы}}',
    description: 'Число работ (агроприёмов: вспашка, посев, полив…)',
  },
  obligation: {
    id: 'common.count.obligation',
    defaultMessage:
      '{count, plural, one {# работа на поле} few {# работы на полях} many {# работ на полях} other {# работы на полях}}',
    description: 'Число работ на полях: поручение одной работы одному полю (контуру) хозяйства; в счётах — «работ на полях»',
  },
  crop: {
    id: 'common.count.crop',
    defaultMessage: '{count, plural, one {# культура} few {# культуры} many {# культур} other {# культуры}}',
    description: 'Число сельхозкультур (хлопок, пшеница…)',
  },
  row: {
    id: 'common.count.row',
    defaultMessage: '{count, plural, one {# строка} few {# строки} many {# строк} other {# строки}}',
    description: 'Число строк таблицы',
  },
  inspection: {
    id: 'common.count.inspection',
    defaultMessage:
      '{count, plural, one {# обследование} few {# обследования} many {# обследований} other {# обследования}}',
    description: 'Число карантинных обследований полей',
  },
  equipmentUnit: {
    id: 'common.count.equipmentUnit',
    defaultMessage: '{count, plural, one {# единица} few {# единицы} many {# единиц} other {# единицы}}',
    description: 'Число единиц техники',
  },
  species: {
    id: 'common.count.species',
    defaultMessage: '{count, plural, one {# вид} few {# вида} many {# видов} other {# вида}}',
    description: 'Число видов карантинных вредителей/болезней/сорняков',
  },
  wave: {
    id: 'common.count.wave',
    defaultMessage: '{count, plural, one {# волна} few {# волны} many {# волн} other {# волны}}',
    description: 'Число волн (очередей) работ',
  },
  campaign: {
    id: 'common.count.campaign',
    defaultMessage: '{count, plural, one {# кампания} few {# кампании} many {# кампаний} other {# кампании}}',
    description: 'Число кампаний (запущенная работа плана с поручениями хозяйствам)',
  },
  finding: {
    id: 'common.count.finding',
    defaultMessage: '{count, plural, one {# находка} few {# находки} many {# находок} other {# находки}}',
    description: 'Число карантинных находок (обнаружений вредного организма)',
  },
  season: {
    id: 'common.count.season',
    defaultMessage: '{count, plural, one {# сезон} few {# сезона} many {# сезонов} other {# сезона}}',
    description: 'Число сезонов',
  },
  cycle: {
    id: 'common.count.cycle',
    defaultMessage: '{count, plural, one {# агроцикл} few {# агроцикла} many {# агроциклов} other {# агроцикла}}',
    description: 'Число агроциклов (культура × сезон: «Хлопок-2026»)',
  },
});

/* ------------------------------------------------------------ знаменатели */

/**
 * Знаменатель в интерфейсе задаётся одной строкой («обязательств хозяйства в
 * цикле»), а согласовать с числом нужно только её первое слово. Ключ словаря —
 * та самая форма, которой знаменатели названы в коде экранов. Только для
 * русских строк-знаменателей; переведённые экраны передают сообщение-счёт
 * без `#` (`{count, plural, one {контур} …}`).
 */
const DENOMINATOR_FORMS: Record<string, [string, string, string]> = {
  // i18n-ignore: русские формы для устаревших строк-знаменателей
  обязательств: ['обязательство', 'обязательства', 'обязательств'],
  // i18n-ignore: русские формы для устаревших строк-знаменателей
  контуров: ['контур', 'контура', 'контуров'],
  // i18n-ignore: русские формы для устаревших строк-знаменателей
  работ: ['работа', 'работы', 'работ'],
  // i18n-ignore: русские формы для устаревших строк-знаменателей
  хозяйств: ['хозяйство', 'хозяйства', 'хозяйств'],
  // i18n-ignore: русские формы для устаревших строк-знаменателей
  массивов: ['массив', 'массива', 'массивов'],
  // i18n-ignore: русские формы для устаревших строк-знаменателей
  культур: ['культура', 'культуры', 'культур'],
  // i18n-ignore: русские формы для устаревших строк-знаменателей
  строк: ['строка', 'строки', 'строк'],
  // i18n-ignore: русские формы для устаревших строк-знаменателей
  обследований: ['обследование', 'обследования', 'обследований'],
  // i18n-ignore: русские формы для устаревших строк-знаменателей
  отчётов: ['отчёт', 'отчёта', 'отчётов'],
  // i18n-ignore: русские формы для устаревших строк-знаменателей
  этапов: ['этап', 'этапа', 'этапов'],
  // i18n-ignore: русские формы для устаревших строк-знаменателей
  // «стадия» → «этап» (словарь ядра): старая подпись знаменателя печатается новым словом.
  // i18n-ignore: русские формы для устаревших строк-знаменателей
  стадий: ['этап', 'этапа', 'этапов'],
  // i18n-ignore: русские формы для устаревших строк-знаменателей
  окон: ['окно', 'окна', 'окон'],
  // i18n-ignore: русские формы для устаревших строк-знаменателей
  заявок: ['заявка', 'заявки', 'заявок'],
  // i18n-ignore: русские формы для устаревших строк-знаменателей
  циклов: ['цикл', 'цикла', 'циклов'],
  // i18n-ignore: русские формы для устаревших строк-знаменателей
  событий: ['событие', 'события', 'событий'],
  // i18n-ignore: русские формы для устаревших строк-знаменателей
  единиц: ['единица', 'единицы', 'единиц'],
  // i18n-ignore: русские формы для устаревших строк-знаменателей
  видов: ['вид', 'вида', 'видов'],
  // i18n-ignore: русские формы для устаревших строк-знаменателей
  волн: ['волна', 'волны', 'волн'],
  // i18n-ignore: русские формы для устаревших строк-знаменателей
  находок: ['находка', 'находки', 'находок'],
  // i18n-ignore: русские формы для устаревших строк-знаменателей
  сезонов: ['сезон', 'сезона', 'сезонов'],
  // i18n-ignore: русские формы для устаревших строк-знаменателей
  поручений: ['поручение', 'поручения', 'поручений'],
};

/**
 * «13 622 обязательства…», «1 контур», «10 812 контуров»: согласует первое
 * слово знаменателя с числом, хвост уточнения оставляет как есть. Незнакомое
 * слово возвращается без изменений — молча ломать текст нельзя. Сообщение
 * каталога форматируется с `count` целиком.
 */
export function denominatorOf(value: number, denominator: string | CountMessage): string {
  if (typeof denominator !== 'string') return tr(denominator as Message<'common.count.contour'>, { count: value });
  const space = denominator.indexOf(' ');
  const head = space < 0 ? denominator : denominator.slice(0, space);
  const forms = DENOMINATOR_FORMS[head];
  if (!forms) return denominator;
  const word = plural(value, forms);
  return space < 0 ? word : `${word}${denominator.slice(space)}`;
}

/* ------------------------------------------------------------------ копия */

/**
 * Первая буква фразы — заглавная. Причины и оговорки пишутся в коде строчными,
 * потому что не знают, встанут ли они после точки; решает это то место, где
 * фраза становится отдельным предложением.
 */
export function sentence(text: string): string {
  if (!text) return text;
  const first = text[0]!;
  const upper = first.toLocaleUpperCase(runtimeLocale() === 'ru' ? 'ru-RU' : 'uz');
  return upper === first ? text : `${upper}${text.slice(1)}`;
}

/**
 * Точка в конце фразы, если её там ещё нет. Сокращение и инициал уже кончаются
 * точкой («Юсупов С.», «17,9 п.п.»), и вторая читается как опечатка.
 */
export function endDot(text: string): string {
  const trimmed = text.trimEnd();
  if (!trimmed) return trimmed;
  return /[.!?…]$/.test(trimmed) ? trimmed : `${trimmed}.`;
}

/* ------------------------------------------------------------------ циклы */

const CYCLE_WORDS = defineMessages({
  title: { id: 'common.cycle.title', defaultMessage: '{crop} {season}', description: 'Имя агроцикла: культура и сезон — «Хлопок 2026»' },
  noCrop: {
    id: 'common.cycle.noCrop',
    defaultMessage: 'Работы без культуры · {season}',
    description: 'Агроцикл работ, не привязанных к культуре (прочие работы сезона)',
  },
  outside: { id: 'common.cycle.outside', defaultMessage: 'Вне агроцикла', description: 'Работа или земля, не относящаяся ни к одному агроциклу' },
  placementUnknown: {
    id: 'common.placement.unknown',
    defaultMessage: 'размещение неизвестно',
    description: 'У контура не названа культура сезона — пробел в данных, а не «пусто»; строчными, встаёт внутрь фразы',
  },
});

/** Культура на случай, если crops.json не прочитан: id стабильны в обоих треках. */
const CROP_FALLBACK: Record<string, string> = {
  wheat: 'GRAIN',
  cotton: 'COTTON',
  potato: 'POTATO',
  feed: 'FODDER',
  fallow: 'FALLOW',
};

/**
 * Имя цикла — культура плюс сезон: «Хлопок 2026», а не голое «2026».
 * Культура — словарём приложения по коду («wheat» → «Пшеница» / «Bugʻdoy»); для кода вне словаря —
 * имя из crops.json; если и его нет — из id цикла («cycle-cotton-2026»), а в последнюю очередь
 * печатается сам код культуры.
 */
export function cycleTitle(
  cycle: Pick<Cycle, 'id' | 'crop' | 'season_label'>,
  crops?: Crop[] | null,
): string {
  // Работы шаблона без культуры (канон: `crop` пуст, ключ `OTHER_CROPS`) —
  // не культура; сырой код печатался на «Сегодня» и в очереди (журнал №546 в).
  if (cycle.crop === 'OTHER_CROPS') return tr(CYCLE_WORDS.noCrop, { season: cycle.season_label });
  // Знакомая культура — словарём приложения по КОДУ: имя из crops.json (фикстура) русское, и на
  // узбекском «Пшеница 2025–2026» читалось бы русским словом среди узбекских («Bugʻdoy 2025–2026»).
  // Имя из справочника — только для кода, которого словарь не знает (как в `cropName`).
  const dictionary = cropLabel((CROP_FALLBACK[cycle.crop] ?? cycle.crop).toUpperCase());
  const known = dictionary.known ? dictionary.label : (crops ?? []).find((c) => c.id === cycle.crop)?.name;
  const guessed =
    known ??
    (CROP_FALLBACK[cycle.crop] ? cropName(CROP_FALLBACK[cycle.crop]) : null) ??
    Object.keys(CROP_FALLBACK).map((key) => (cycle.id.includes(key) ? cropName(CROP_FALLBACK[key]) : null)).find(Boolean) ??
    cycle.crop;
  return tr(CYCLE_WORDS.title, { crop: guessed, season: cycle.season_label });
}

/**
 * Название цикла по ключу «сезон:КОД» (`2026:COTTON`, `2026-2027:GRAIN`) — культура
 * словарём приложения по КОДУ, а не строкой сервера. Сервер называет цикл на
 * своём языке («Хлопок 2026»), и на узбекском такое имя читалось бы русским
 * словом среди узбекских; по коду то же имя собирается на языке интерфейса
 * («Paxta 2026»), как у остальных подписей цикла. Код вне словаря — строка сервера:
 * лучше чужое слово, чем «неизвестно: X».
 */
export function cycleTitleOfKey(key: string | null | undefined, serverTitle?: string | null): string | null {
  if (!key) return serverTitle ?? null;
  const [season, code] = key.split(':');
  if (!season || !code) return serverTitle ?? key;
  if (!cropLabel(code.toUpperCase()).known) return serverTitle ?? key;
  return tr(CYCLE_WORDS.title, { crop: cropName(code), season });
}

/**
 * Название цикла по одному идентификатору, когда самой записи цикла под рукой
 * нет (работа ссылается на цикл, которого нет в списке). Канонический ключ
 * строится как `<КУЛЬТУРА>-<сезон>` (`cycleIdOf` в `data-source/api/canon.ts`):
 * из него читается «Прочие культуры 2025-2026», а не код `OTHER_CROPS-2025-2026`.
 * Ключ другого вида печатается как есть — выдумывать название нельзя.
 */
export function cycleNameFromId(id: string, crops?: Crop[] | null): string {
  if (id === 'vne-cikla') return tr(CYCLE_WORDS.outside);
  const m = /^([A-Z][A-Z_]*)-(\d{4}(?:-\d{4})?)$/.exec(id);
  if (!m) return id;
  const known = (crops ?? []).find((c) => c.id === m[1])?.name;
  return tr(CYCLE_WORDS.title, { crop: known ?? cropLabel(m[1]).label, season: m[2] ?? '' });
}

/**
 * Контур без культуры сезона — это пробел в данных, а не факт «пусто».
 * Формулировка одна на все экраны, чтобы «не размещено» не читалось как ноль.
 */
export function placementUnknown(): string {
  return tr(CYCLE_WORDS.placementUnknown);
}
